import { call, put, all } from "redux-saga/effects";
import {
  getWarehouseKPIData,
  getStoreKPIData,
  getWarehouseKPIDataRetailOps,
  getStoreKPIDataRetailOps,
  getFilters,
  getProductDescription,
  getCICDetails,
  getDefaultFilters,
} from "../end-points/dashboard";

import {
  fetchUnitsData,
  fetchUnitsDataSuccess,
  fetchUnitsDataError,
  fetchFilterOptions,
  fetchFilterOptionsSuccess,
  fetchMultipleFilterOptionsSuccess,
  fetchFilterOptionsError,
  fetchProductDescription,
  fetchCICAndUPCDetails,
  fetchCICAndUPCDetailsSuccess,
  fetchCICAndUPCDetailsPartialSuccess,
  fetchCICAndUPCDetailsError,
  setItemTypeAction,
  fetchDefaultFilters,
  fetchDefaultFiltersSuccess,
  fetchDefaultFiltersError,
  toggleToastMessage,
  setIsDetectedLocation,
  updateFilterUpdateKey,
} from "../modules/dashboard";
import { formatFilterOptions } from "../../utils/formatFilterOptions";
import { ISuccessResponse } from "../../types/response.types";
import { IDropdownOption, IFilterOption } from "../../types/dropdown.types";
import { IFilter, IFilterOptions } from "../../types/filters.types";
import { IUnits, IUnitsTotalData, ItemType } from "../../types/data.types";

interface WarehouseKPIData {
  getUnifiedWarehouseKPIValues: {
    itemType: ItemType;
    onOrderSellingUnits?: IUnits;
    inTransitToDcSellingUnits?: IUnits;
    dcInventorySellingUnits?: IUnits;
    onOrderShippingUnits?: IUnits;
    inTransitToDcShippingUnits?: IUnits;
    dcInventoryShippingUnits?: IUnits;
    onOrderVendorUnits?: IUnits;
    inTransitToDcVendorUnits?: IUnits;
    dcInventoryVendorUnits?: IUnits;
  } & IUnitsTotalData;
}

interface StoreKPIData {
  getUnifiedStoreKPIValues: {
    itemType: ItemType;
    inTransitToStoreSellingUnits?: IUnits;
    storeInventorySellingUnits?: IUnits;
    inTransitToStoreShippingUnits?: IUnits;
    storeInventoryShippingUnits?: IUnits;
    inTransitToStoreVendorUnits?: IUnits;
    storeInventoryVendorUnits?: IUnits;
    storeOnOrderSellingUnits?: IUnits;
    storeOnOrderShippingUnits?: IUnits;
    storeOnOrderVendorUnits?: IUnits;
  } & IUnitsTotalData;
}

export const transformFilterOptions = (
  filterOptions: IFilterOptions,
): IFilter => ({
  warehouseDivision: formatFilterOptions(
    filterOptions.warehouseDivision,
    "label",
  ),
  warehouse: formatFilterOptions(filterOptions.warehouse, "value"),
  distribution: formatFilterOptions(filterOptions.distribution, "label"),
  dsdVendor: formatFilterOptions(filterOptions.dsdVendor, "value"),
  retailDivision: formatFilterOptions(filterOptions.retailDivision, "label"),
  retailStore: formatFilterOptions(filterOptions.retailStore, "label"),
  smic: formatFilterOptions(filterOptions.smic, "label"),
  upc: formatFilterOptions(filterOptions.upc, "value"),
  cic: filterOptions.cic.length > 0 ? `${filterOptions.cic[0].value}` : "",
  department: formatFilterOptions(filterOptions.department, "value"),
  retailSection: formatFilterOptions(filterOptions.retailSection, "value"),
});

export function* getWarehouseKPIDataSaga(
  action: ReturnType<typeof fetchUnitsData>,
): any {
  const { filters, isRetailOps } = action.value;
  try {
    const dcFacilityInventoryResponse: ISuccessResponse<WarehouseKPIData> =
      yield call(
        isRetailOps ? getWarehouseKPIDataRetailOps : getWarehouseKPIData,
        transformFilterOptions(filters),
      );
    const data = dcFacilityInventoryResponse.data.getUnifiedWarehouseKPIValues;
    const sellingUnits = {
      dcInventory: data.dcInventorySellingUnits,
      inTransitToDC: data.inTransitToDcSellingUnits,
      onOrder: data.onOrderSellingUnits,
    };
    const shippingUnits = {
      dcInventory: data.dcInventoryShippingUnits,
      inTransitToDC: data.inTransitToDcShippingUnits,
      onOrder: data.onOrderShippingUnits,
    };
    const vendorUnits = {
      dcInventory: data.dcInventoryVendorUnits,
      inTransitToDC: data.inTransitToDcVendorUnits,
      onOrder: data.onOrderVendorUnits,
    };
    const total = {
      fourWeekFrwdDemandSellingUnits: data.fourWeekFrwdDemandSellingUnits,
      eightWeekHstrSelnSellingUnits: data.eightWeekHstrSelnSellingUnits,
      twentySixWeekHstrSalesSellingUnits:
        data.twentySixWeekHstrSalesSellingUnits,
      fourWeekFrwdDemandShippingUnits: data.fourWeekFrwdDemandShippingUnits,
      eightWeekHstrSelnShippingUnits: data.eightWeekHstrSelnShippingUnits,
      twentySixWeekHstrSalesShippingUnits:
        data.twentySixWeekHstrSalesShippingUnits,
      fourWeekFrwdDemandVendorUnits: data.fourWeekFrwdDemandVendorUnits,
      eightWeekHstrSelnVendorUnits: data.eightWeekHstrSelnVendorUnits,
      twentySixWeekHstrSalesVendorUnits: data.twentySixWeekHstrSalesVendorUnits,
    };
    yield put(
      fetchUnitsDataSuccess({
        sellingUnits,
        shippingUnits,
        vendorUnits,
        totalWarehouseData: total,
        totalStoreData: {},
      }),
    );
    if (data.itemType.type === "UNIFIED") {
      const itemType =
        filters.warehouseDivision.length > 0 || filters.warehouse.length > 0
          ? ({
              type: "WAREHOUSE",
              subType: "FAR",
            } as ItemType)
          : data.itemType;
      yield put(setItemTypeAction(itemType));
    } else {
      yield put(setItemTypeAction(data.itemType));
    }
  } catch (error: any) {
    console.log("getWarehouseKPIDataSaga Error:", error);
    yield put(fetchUnitsDataError());
  }
}

export function* getStoreKPIDataSaga(
  action: ReturnType<typeof fetchUnitsData>,
): any {
  const { filters, isRetailOps } = action.value;
  try {
    const dcFacilityInventoryResponse: ISuccessResponse<StoreKPIData> =
      yield call(
        isRetailOps ? getStoreKPIDataRetailOps : getStoreKPIData,
        transformFilterOptions(filters),
      );
    const data = dcFacilityInventoryResponse.data.getUnifiedStoreKPIValues;
    const sellingUnits = {
      inTransitToStore: data.inTransitToStoreSellingUnits,
      storeInventory: data.storeInventorySellingUnits,
      dsdOnOrder: data.storeOnOrderSellingUnits,
    };
    const shippingUnits = {
      inTransitToStore: data.inTransitToStoreShippingUnits,
      storeInventory: data.storeInventoryShippingUnits,
      dsdOnOrder: data.storeOnOrderShippingUnits,
    };
    const vendorUnits = {
      inTransitToStore: data.inTransitToStoreVendorUnits,
      storeInventory: data.storeInventoryVendorUnits,
      dsdOnOrder: data.storeOnOrderVendorUnits,
    };
    const total = {
      fourWeekFrwdDemandSellingUnits: data.fourWeekFrwdDemandSellingUnits,
      eightWeekHstrSelnSellingUnits: data.eightWeekHstrSelnSellingUnits,
      twentySixWeekHstrSalesSellingUnits:
        data.twentySixWeekHstrSalesSellingUnits,
      fourWeekFrwdDemandShippingUnits: data.fourWeekFrwdDemandShippingUnits,
      eightWeekHstrSelnShippingUnits: data.eightWeekHstrSelnShippingUnits,
      twentySixWeekHstrSalesShippingUnits:
        data.twentySixWeekHstrSalesShippingUnits,
      fourWeekFrwdDemandVendorUnits: data.fourWeekFrwdDemandVendorUnits,
      eightWeekHstrSelnVendorUnits: data.eightWeekHstrSelnVendorUnits,
      twentySixWeekHstrSalesVendorUnits: data.twentySixWeekHstrSalesVendorUnits,
    };
    yield put(
      fetchUnitsDataSuccess({
        sellingUnits,
        shippingUnits,
        vendorUnits,
        totalWarehouseData: {},
        totalStoreData: total,
      }),
    );
  } catch (error: any) {
    console.log("getStoreKPIDataSaga Error:", error);
    yield put(fetchUnitsDataError());
  }
}

export function* getFilterOptionsSaga(
  action: ReturnType<typeof fetchFilterOptions>,
): any {
  const { type, query, allFilters, pageNumber, filters } = action.value;
  try {
    if (Array.isArray(type)) {
      const allOptions: IFilterOptions = { ...allFilters };
      const pageStateUpdates: Record<
        string,
        { page: number; hasMore: boolean }
      > = {};
      for (const filterType of type) {
        const response: ISuccessResponse<{
          data: IFilterOption[];
          hasMore: boolean;
        }> = yield call(getFilters, filterType, query, pageNumber, filters);
        allOptions[filterType] = response.data.data;
        pageStateUpdates[filterType] = {
          page: pageNumber,
          hasMore: response.data.hasMore,
        };
      }
      yield put(
        fetchMultipleFilterOptionsSuccess(allOptions, type, pageStateUpdates),
      );
    } else {
      const response: ISuccessResponse<{
        data: IFilterOption[];
        hasMore: boolean;
      }> = yield call(getFilters, type, query, pageNumber, filters);

      yield put(
        fetchFilterOptionsSuccess(
          type,
          response.data.data,
          pageNumber,
          response.data.hasMore,
        ),
      );
    }
  } catch (error: any) {
    console.log("getFilterOptionsSaga Error:", error);
    yield put(fetchFilterOptionsError(type));
  }
}

export function* getDefaultFiltersSaga(
  action: ReturnType<typeof fetchDefaultFilters>,
): any {
  const { location, extraFilters } = action.value;
  try {
    const response: ISuccessResponse<Partial<IFilterOptions>> = yield call(
      getDefaultFilters,
      location,
      extraFilters,
    );

    yield put(fetchDefaultFiltersSuccess(response.data, extraFilters));
    yield put(
      toggleToastMessage({
        show: true,
        variant: "success",
        message: "Filters updated based on your location!",
      }),
    );
    yield put(setIsDetectedLocation(true));
  } catch (error: any) {
    console.log("getDefaultFiltersSaga Error:", error);
    yield put(fetchDefaultFiltersError(extraFilters));
    yield put(setIsDetectedLocation(true));
  }
}

export function* getCICDetailsSaga(
  action: ReturnType<typeof fetchCICAndUPCDetails>,
): any {
  const { data, type, retailStore } = action.value;
  try {
    const descResponse: ISuccessResponse<{ cic: string; description: string }> =
      yield call(getProductDescription, data, type);
    const { cic: resolvedCic, description } = descResponse.data;

    yield put(
      fetchProductDescription([
        { label: resolvedCic, value: resolvedCic, description },
      ]),
    );

    const response: ISuccessResponse<{
      upcs: IDropdownOption[];
      itemType: ItemType;
    }> = yield call(getCICDetails, resolvedCic, "cic");

    yield put(
      fetchCICAndUPCDetailsPartialSuccess(
        { cic: resolvedCic, description, ...response.data },
        type,
      ),
    );

    if (retailStore.length > 0 && response.data.itemType.type !== "DSD") {
      const cicFilter: IFilter = {
        warehouseDivision: "",
        warehouse: "",
        distribution: "",
        dsdVendor: "",
        retailDivision: "",
        retailStore: retailStore.map((s) => s.value).join(","),
        smic: "",
        upc: response.data.upcs.map((u) => u.value).join(","),
        cic: resolvedCic,
        department: "",
        retailSection: "",
      };
      const [wdResponse, wResponse]: [
        ISuccessResponse<{ data: IFilterOption[]; hasMore: boolean }>,
        ISuccessResponse<{ data: IFilterOption[]; hasMore: boolean }>,
      ] = yield all([
        call(getFilters, "warehouseDivision", "", 0, cicFilter),
        call(getFilters, "warehouse", "", 0, cicFilter),
      ]);
      const warehouseDivisions = wdResponse.data.data;
      const warehouses = wResponse.data.data;

      yield put(fetchCICAndUPCDetailsSuccess(warehouseDivisions, warehouses));
    } else {
      yield put(updateFilterUpdateKey());
    }
  } catch (error: any) {
    console.log("getCICDetailsSaga Error:", error);
    yield put(fetchCICAndUPCDetailsError(type));
  }
}
